/* eslint-disable camelcase */
import mixpanel from "mixpanel-browser";
import {
  adData,
  getLangCode,
  mixpanelProjectToken,
  userRoles,
  abTestConfig,
  sessionGameplaysKey,
} from "../config/index";
import { mobile } from "./detection";
import { language } from "../config";
import routeCodes from "../config/routeCodes";
import { convertEPOCHDate } from "./index";
import { abTestLocalStorageKey } from "../config/index";

const developers = "in-house";

// Initialize Mixpanel
export const initMixpanel = () => {
  mixpanel.init(mixpanelProjectToken, {
    debug: process.env.NODE_ENV !== "production",
    persistence: "localStorage",
  });

  // Add Super Properties for Mixpanel, which are shared by all events. Some of these may be updated later.
  mixpanel.register({
    is_mobile: mobile,
    ui_lang: getLangCode() === language.default ? language.default : "es",
  });
};

// Mapping from ABcya userType selected during onboarding to Mixpanel user_role
export const getMixpanelUserRole = (userType) => {
  switch (userType) {
    case "STUDENT":
      return "student";
    case "TEACHER":
      return "teacher";
    case "PARENT":
      return "parent";
    default:
      return "anonymous";
  }
};

// Mapping from ABcya planGroup to Mixpanel membership_type
export const getMixpanelMembershipType = (planGroup) => {
  switch (planGroup) {
    case userRoles.free:
      return "free";
    case userRoles.classroom:
      return "premium classroom";
    case userRoles.school:
      return "premium school";
    case userRoles.family:
      return "premium family";
    default:
      return "none";
  }
};

// Count ad elements on the page
const countAdElements = () => {
  let totalCount = 0;

  const hasContent = (elementId) => {
    const element = document.querySelector(`#${elementId}`);
    return element && element.innerHTML.trim() !== "";
  };

  // Count desktop and mobile leader ads
  totalCount += [...adData.divIds.leader.desktop, ...adData.divIds.leader.mobile].reduce(
    (sum, position) => sum + (hasContent(position) ? 1 : 0),
    0,
  );
  // Count sky ads
  totalCount += Object.values(adData.divIds.sky).reduce((sum, position) => sum + (hasContent(position) ? 1 : 0), 0);
  // Count billboard ads
  totalCount += hasContent(adData.divIds.billboard) ? 1 : 0;
  // Count adhesion ads
  totalCount += hasContent(adData.divIds.adhesion) ? 1 : 0;
  // Count video ads
  totalCount += Object.values(adData.divIds.video).reduce((sum, position) => sum + (hasContent(position) ? 1 : 0), 0);

  return totalCount;
};

const billingFrequency = (months) => {
  switch (parseInt(months)) {
    case 1:
      return "monthly";
    case 6:
      return "semiannually";
    case 12:
      return "annually";
    default:
      return "unknown";
  }
};

const getCurrentUrl = (pathname) => {
  return `${window.location.protocol}//${window.location.host}${pathname}`;
};

export const setUserPropertyNumAdsShown = () => {
  // set a timeout to count the number of ads shown
  try {
    setTimeout(() => {
      const numOfAds = countAdElements();
      mixpanel.people.set_once({
        ads_shown: 0,
      });
      mixpanel.people.increment("ads_shown", numOfAds);
    }, 2000);
  } catch (error) {}
};

// Track page view event
export const mpPageView = () => {
  try {
    // Track total pageviews using localStorage
    let totalPageviews = localStorage.getItem("total_pageviews");
    if (!totalPageviews) {
      localStorage.setItem("total_pageviews", 1);
      totalPageviews = 1;
    } else {
      localStorage.setItem("total_pageviews", String(Number(totalPageviews) + 1));
      totalPageviews = Number(totalPageviews) + 1;
    }

    // Check if pageviewcount exists in sessionStorage. (pageviewcount is set by Mixpanel on the first pageview.)
    let sessionPageviews = sessionStorage.getItem("pageviewcount");
    if (!sessionPageviews) {
      sessionPageviews = 1;
    } else {
      sessionPageviews = Number(sessionPageviews) + 1;
    }

    // Set the same delay as gtmPageView to allow Helmet to correctly set the page title
    setTimeout(() => {
      mixpanel.track_pageview({
        session_pageviews: sessionPageviews,
        total_pageviews: totalPageviews,
      });
    }, 100);
  } catch (e) {}
};

export const mpUserIdentify = (userId) => {
  mixpanel.identify(userId);
};

export const mpSetUserProperties = (properties) => {
  mixpanel.people.set(properties);
};

export const mpUserIdentifyAndSetProperties = (userId, properties) => {
  mixpanel.identify(userId);
  mixpanel.people.set(properties);
};

export const mpUserReset = () => {
  mixpanel.reset();
  // Set A/B Test-related super properties again after resetting Mixpanel
  try {
    const mpABTests = JSON.parse(localStorage.getItem(abTestLocalStorageKey));
    if (mpABTests) {
      const expCookie = Object.values(mpABTests).reduce(
        (cookie, testVariant) => ({
          ...cookie,
          [testVariant.testName]: testVariant.testVariantName,
        }),
        {},
      );
      mpRegisterEventProperties({
        experiments_cookie: expCookie,
      });
    }
  } catch (error) {
    console.log("Error in mpUserReset: ", error);
  }
};

export const mpRegisterEventProperties = (properties) => {
  try {
    mixpanel.register(properties);
  } catch (error) {}
};

export const mpLoginSuccess = (
  tokenRole,
  loginCount,
  isLoginByForm,
  userType,
  planGroup,
  userId,
  createdAt,
  pathname,
) => {
  try {
    const mpUserRole = getMixpanelUserRole(userType);
    const mpMembershipType = getMixpanelMembershipType(planGroup);
    mpUserIdentifyAndSetProperties(userId, {
      signup_date: createdAt,
      signup_language: getLangCode() === language.default ? language.default : "es",
      user_role: tokenRole === userRoles.student ? "student" : mpUserRole,
      membership_type: mpMembershipType,
      last_active_date: new Date().toISOString(),
    });
    mpRegisterEventProperties({
      user_role: tokenRole === userRoles.student ? "student" : mpUserRole,
      membership_type: mpMembershipType,
    });

    const isNewSignup = window.localStorage.getItem("isNewSignup") === "1";
    window.localStorage.removeItem("isNewSignup"); // remove it after checking
    let loginMethod;
    let loginPagePath;
    let loginDisplayType;
    // manual login vs token-based login
    if (isLoginByForm) {
      loginMethod = tokenRole === "STUDENT" ? "access code" : "password";
      // new signup vs returning user
      if (isNewSignup) {
        switch (tokenRole) {
          case userRoles.free:
            loginPagePath = routeCodes.SUBSCRIBE_FREE;
            break;
          case userRoles.family:
            loginPagePath = routeCodes.SUBSCRIBE_FAMILY;
            break;
          case userRoles.classroom:
            loginPagePath = routeCodes.SUBSCRIBE_CLASSROOM;
            break;
          case userRoles.school:
            loginPagePath = routeCodes.SUBSCRIBE_ABCYA_SCHOOL;
            break;
          default:
            loginPagePath = "/";
        }
        loginDisplayType = "page";
      } else {
        loginPagePath = window.location.pathname;
        loginDisplayType = "modal";
      }
    } else {
      loginMethod = "token-based";
      loginPagePath = window.location.pathname;
      loginDisplayType = "auto-login";
    }

    mixpanel.track("Login Success", {
      login_origin_url: window.location.origin + loginPagePath,
      page_url_path: loginPagePath,
      login_display_type: loginDisplayType,
      login_count: loginCount,
      login_method: loginMethod,
      $current_url: getCurrentUrl(pathname),
    });
    mixpanel.people.set_once({
      total_sessions: 0,
    });
    mixpanel.people.increment("total_sessions", 1);
  } catch (e) {}
};

export const mpPaymentSubmitted = (subData) => {
  try {
    const months = parseInt(subData.months.slice(1)); // "m1" -> 1, "m6" -> 6, "m12" -> 12

    mixpanel.track("Payment Submitted", {
      subscription_origin_url: window.location.href,
      page_url_path: window.location.pathname,
      subscription_plan_type: getMixpanelMembershipType(subData.planGroup),
      subscription_billing_frequency: billingFrequency(months),
      promo_code_used: subData.isPromoCodeUsed,
      price: subData.amount,
      $current_url: getCurrentUrl(subData.pathname),
    });
  } catch (e) {}
};

export const mpModalClicked = (modalData) => {
  try {
    mixpanel.track("Modal Clicked", {
      page_title: document.title,
      page_url_path: window.location.pathname,
      modal_type: modalData.modalType || "",
      modal_trigger_type: modalData.triggerType || "",
      modal_header: modalData.header || "",
      modal_primary_cta: modalData.primaryCta || "",
      modal_secondary_cta: modalData.secondaryCta || "",
      modal_tertiary_cta: modalData.tertiaryCta || "",
      modal_is_dismissible: modalData.isDismissible || false,
      modal_interaction: modalData.interaction || "",
      $current_url: getCurrentUrl(modalData.pathname),
    });
  } catch (e) {}
};

export const mpModalShown = (modalData) => {
  try {
    mixpanel.track("Modal Shown", {
      page_title: document.title,
      page_url_path: window.location.pathname,
      modal_type: modalData.modalType || "",
      modal_trigger_type: modalData.triggerType || "",
      modal_header: modalData.header || "",
      modal_primary_cta: modalData.primaryCta || "",
      modal_secondary_cta: modalData.secondaryCta || "",
      modal_tertiary_cta: modalData.tertiaryCta || "",
      modal_is_dismissible: modalData.isDismissible || false,
      $current_url: getCurrentUrl(modalData.pathname),
    });
  } catch (e) {}
};

export const mpSignUpSuccess = (signupDisplayType, planGroup, pathname) => {
  try {
    window.localStorage.setItem("isNewSignup", "1"); // used for Login Success event to check if it's a new signup
    const mpMembershipType = getMixpanelMembershipType(planGroup);
    mixpanel.track("Signup Success", {
      signup_origin_url: window.location.href,
      page_url_path: window.location.pathname,
      signup_display_type: signupDisplayType,
      membership_type: mpMembershipType,
      $current_url: getCurrentUrl(pathname),
    });
    mixpanel.people.set({
      signup_date: new Date().toISOString(),
      signup_language: getLangCode(),
    });
  } catch (e) {
    console.log("Error in mpSignUpSuccess: ", e);
  }
};

export const mpSubCTASelect = (feature, ctaLocation, ctaText, pathname) => {
  try {
    mixpanel.track("Subscription CTA Selected", {
      page_title: document.title,
      locked_feature: feature,
      subscription_cta_location: ctaLocation,
      subscription_cta_text: ctaText,
      page_url_path: window.location.pathname,
      $current_url: getCurrentUrl(pathname),
    });
    mpRegisterEventProperties({
      last_subscription_cta_location: ctaLocation,
      last_subscription_cta_text: ctaText,
    });
    mpSetUserProperties({
      last_subscription_cta_location: ctaLocation,
    });
  } catch (e) {
    console.log("Error in mpSubCTASelect: ", e);
  }
};

export const mpSubStarted = (subData) => {
  try {
    const mpMembershipType = getMixpanelMembershipType(subData.planGroup);

    mpRegisterEventProperties({
      membership_type: mpMembershipType,
    });

    mixpanel.track("Subscription Started", {
      subscription_billing_frequency: billingFrequency(subData.months),
      subscription_plan_type: mpMembershipType,
      subscription_price: subData.amount,
      subscription_start_type: subData.startType,
      subscription_term_start: convertEPOCHDate(Number(subData.periodStart), language.default),
      subscription_term_end: convertEPOCHDate(Number(subData.periodEnd), language.default),
      subscription_origin_url: window.location.href,
      page_url_path: window.location.pathname,
      $current_url: getCurrentUrl(subData.pathname),
    });

    mpSetUserProperties({
      subscription_billing_frequency: billingFrequency(subData.months),
      subscription_purchased_in_web: true,
      subscription_renewal_status: "auto renewing",
      subscription_term_start: convertEPOCHDate(Number(subData.periodStart), language.default),
      subscription_term_end: convertEPOCHDate(Number(subData.periodEnd), language.default),
      subscription_status: "active",
      membership_type: mpMembershipType,
    });

    mixpanel.people.set_once({
      subscription_original_start_date: convertEPOCHDate(Number(subData.subStartDate), language.default),
    });
  } catch (e) {
    console.log("Error in mpSubStarted: ", e);
  }
};

const daysUntil = (epochTime) => {
  const now = Date.now(); // Get current time in milliseconds
  const diff = epochTime * 1000 - now; // Convert epochTime to milliseconds and find difference
  return Math.ceil(diff / (1000 * 60 * 60 * 24)); // Convert to days and round up
};

export const mpSubAutoRenewCancelled = (subData) => {
  try {
    mixpanel.track("Subscription Autorenew Cancelled", {
      subscription_time_remaining: daysUntil(Number(subData.periodEnd)),
      subscription_purchase_platform: "website",
      subscription_renewal_count: subData.countOfRenewalInvoices,
      subscription_original_start_date: convertEPOCHDate(Number(subData.subStartDate), language.default),
      subscription_term_start: convertEPOCHDate(Number(subData.periodStart), language.default),
      subscription_term_end: convertEPOCHDate(Number(subData.periodEnd), language.default),
      subscription_billing_frequency: billingFrequency(subData.months),
      subscription_plan_type: getMixpanelMembershipType(subData.planGroup),
      $current_url: getCurrentUrl(subData.pathname),
    });

    mpSetUserProperties({
      subscription_renewal_status: "canceled",
    });
  } catch (e) {
    console.log("Error in mpSubAutoRenewCancelled: ", e);
  }
};

export const mpGameStarted = (game, isReplay, gameHasCompletionState, wasCompleted) => {
  const { genre, id, subject, theme, category, fullname, gamePageCurrentURL } = game;
  try {
    let sessionPlayCount = Number(window.sessionStorage.getItem(sessionGameplaysKey)) || 0;
    window.sessionStorage.setItem(sessionGameplaysKey, String(++sessionPlayCount));

    mixpanel.track("Game Started", {
      game_genre: genre,
      game_id: id,
      is_replay: isReplay,
      game_subject: subject,
      game_theme: theme,
      game_category: category,
      game_name: fullname,
      developers,
      session_gameplays: sessionPlayCount,
      game_has_completion_state: gameHasCompletionState,
      game_has_been_completed: wasCompleted,
      $current_url: gamePageCurrentURL,
    });
  } catch (e) {
    console.log("Error in mpGameStarted: ", e);
  }
};

export const mpPlayPressed = (game) => {
  const { genre, id, subject, theme, category, fullname, gamePageCurrentURL } = game;
  try {
    const sessionPlayCount = Number(window.sessionStorage.getItem(sessionGameplaysKey)) || 0;

    mixpanel.track("Play Button Pressed", {
      game_genre: genre,
      game_id: id,
      game_subject: subject,
      game_theme: theme,
      game_category: category,
      game_name: fullname,
      developers,
      session_gameplays: sessionPlayCount,
      $current_url: gamePageCurrentURL,
    });
  } catch (e) {
    console.log("Error in mpPlayPressed: ", e);
  }
};

export const mpGamePaused = (game, pauseReason, gameHasCompletionState, wasCompleted) => {
  const { genre, id, subject, theme, category, fullname, gamePageCurrentURL } = game;
  try {
    mixpanel.track("Game Paused", {
      game_genre: genre,
      game_id: id,
      game_subject: subject,
      game_theme: theme,
      game_category: category,
      game_name: fullname,
      developers,
      pause_reason: pauseReason,
      game_has_completion_state: gameHasCompletionState,
      game_has_been_completed: wasCompleted,
      $current_url: gamePageCurrentURL,
    });
  } catch (e) {
    console.log("Error in mpGamePaused: ", e);
  }
};

export const mpGameResumed = (game, timeElapsedSeconds, gameHasCompletionState, wasCompleted) => {
  const { genre, id, subject, theme, category, fullname, gamePageCurrentURL } = game;
  try {
    mixpanel.track("Game Resumed", {
      game_genre: genre,
      game_id: id,
      game_subject: subject,
      game_theme: theme,
      game_category: category,
      game_name: fullname,
      developers,
      time_elapsed_seconds: timeElapsedSeconds,
      game_has_completion_state: gameHasCompletionState,
      game_has_been_completed: wasCompleted,
      $current_url: gamePageCurrentURL,
    });
  } catch (e) {
    console.log("Error in mpGameResumed: ", e);
  }
};

export const mpGameCompleted = (game, activePlayTimeSeconds, wasCompleted, completionOutcome) => {
  const { genre, id, subject, theme, category, fullname, gamePageCurrentURL } = game;
  try {
    mixpanel.track("Game Completed", {
      game_genre: genre,
      game_id: id,
      game_subject: subject,
      game_theme: theme,
      game_category: category,
      game_name: fullname,
      developers,
      active_play_time_seconds: activePlayTimeSeconds,
      game_has_been_completed: wasCompleted,
      completion_outcome: completionOutcome,
      $current_url: gamePageCurrentURL,
    });
  } catch (e) {
    console.log("Error in mpGameCompleted: ", e);
  }
};

export const mpLevelCompleted = (
  game,
  activePlayTimeSeconds,
  level,
  totalGameLevels,
  attempts,
  wasCompleted,
  completionOutcome,
) => {
  const { genre, id, subject, theme, category, fullname, gamePageCurrentURL } = game;
  try {
    mixpanel.track("Level Completed", {
      game_genre: genre,
      game_id: id,
      game_subject: subject,
      game_theme: theme,
      game_category: category,
      game_name: fullname,
      developers,
      active_play_time_seconds: activePlayTimeSeconds,
      level,
      total_game_levels: totalGameLevels,
      attempts,
      game_has_been_completed: wasCompleted,
      completion_outcome: completionOutcome,
      $current_url: gamePageCurrentURL,
    });
  } catch (e) {
    console.log("Error in mpLevelCompleted: ", e);
  }
};

export const mpGameAbandoned = (game, activePlayTimeSeconds, exitMethod, gameHasCompletionState, wasCompleted) => {
  try {
    const { genre, id, subject, theme, category, fullname, gamePageCurrentURL } = game;

    mixpanel.track("Game Abandoned", {
      active_play_time_seconds: activePlayTimeSeconds,
      developers,
      exit_method: exitMethod,
      game_genre: genre,
      game_id: id,
      game_subject: subject,
      game_theme: theme,
      game_category: category,
      game_name: fullname,
      game_has_completion_state: gameHasCompletionState,
      game_has_been_completed: wasCompleted,
      $current_url: gamePageCurrentURL,
    });
  } catch (e) {
    console.log("Error in mpGameAbandoned: ", e);
  }
};

export const mpTimeOnPage = (timeOnPageSeconds, scrollDepth, numAdsShown, isBlocking, numAdsOnPage, pathname) => {
  try {
    mixpanel.track("Time On Page", {
      time_on_page_seconds: timeOnPageSeconds,
      scroll_depth: scrollDepth,
      num_ads_shown: numAdsShown,
      is_blocking: isBlocking,
      num_ads_on_page: numAdsOnPage,
      $current_url: getCurrentUrl(pathname),
    });
  } catch (e) {
    console.log("Error in mpTimeOnPage: ", e);
  }
};

export const mpGameToolbarAction = (game, actionType) => {
  try {
    const { genre, id, subject, theme, category, fullname, gamePageCurrentURL } = game;

    mixpanel.track("Game Toolbar Action Triggered", {
      action_type: actionType,
      developers,
      game_genre: genre,
      game_id: id,
      game_subject: subject,
      game_theme: theme,
      game_category: category,
      game_name: fullname,
      $current_url: gamePageCurrentURL,
    });
  } catch (e) {
    console.log("Error in mpGameToolbarAction: ", e);
  }
};

export const mpGameAnalytics = (game, eventName, props) => {
  try {
    const { genre, id, subject, theme, category, fullname, gamePageCurrentURL } = game;

    mixpanel.track(eventName, {
      ...props,
      developers,
      game_genre: genre,
      game_id: id,
      game_subject: subject,
      game_theme: theme,
      game_category: category,
      game_name: fullname,
      $current_url: gamePageCurrentURL,
    });
  } catch (e) {
    console.log("Error in mpGameAnalytics: ", e);
  }
};

// An utility function to find or assign the device into one of two groups for A/B testing
// If the game is part of an active A/B test, return an object with all active A/B tests and their assigned variants
export const assignDeviceForABTest = (gameShortname) => {
  try {
    if (abTestConfig[gameShortname]) {
      const mp_ab_test = JSON.parse(localStorage.getItem(abTestLocalStorageKey)) || {};
      // If the test is already assigned to a device in local storage, return all test variants
      if (mp_ab_test[gameShortname]) {
        return mp_ab_test;
      }

      // If the test is not assigned to a device in local storage, randomly assign one, and save
      let gameVariant = {};
      const config = abTestConfig[gameShortname];
      const rand = Math.random();
      if (rand < config.variants.groupA.percentage) {
        gameVariant = {
          testName: config.testName,
          testVariantName: config.variants.groupA.name,
          gameVersion: config.variants.groupA.gameVersion,
        };
      } else {
        gameVariant = {
          testName: config.testName,
          testVariantName: config.variants.groupB.name,
          gameVersion: config.variants.groupB.gameVersion,
        };
      }
      mp_ab_test[gameShortname] = gameVariant;
      localStorage.setItem(abTestLocalStorageKey, JSON.stringify(mp_ab_test));
      return mp_ab_test;
    }
    return null;
  } catch (error) {
    console.log("Error in assignDeviceForABTest: ", error);
    return null;
  }
};

export const cleanABTestLocalStorage = () => {
  try {
    // remove all entries whose key (i.e. gameShortname) is not in the abTestConfig
    const mpABTests = JSON.parse(localStorage.getItem(abTestLocalStorageKey) || "{}");
    const config = Object.keys(abTestConfig).reduce((remainingTests, gameShortname) => {
      if (mpABTests[gameShortname]) {
        return { ...remainingTests, [gameShortname]: mpABTests[gameShortname] };
      }
      return remainingTests;
    }, {});
    if (Object.keys(config).length > 0) {
      localStorage.setItem(abTestLocalStorageKey, JSON.stringify(config));
      // Update the super property "experiments_cookie"
      const expCookie = Object.values(config).reduce(
        (cookie, testVariant) => ({
          ...cookie,
          [testVariant.testName]: testVariant.testVariantName,
        }),
        {},
      );
      mpRegisterEventProperties({
        experiments_cookie: expCookie,
      });
    } else {
      localStorage.removeItem(abTestLocalStorageKey);
      mixpanel.unregister("experiments_cookie");
    }
  } catch (error) {
    console.log("Error in cleanABTestLocalStorage: ", error);
  }
};
